import { useState } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { CompactProjectSelect } from '@/pages/Dashboard';
import { CompactPageSelect } from '@/pages/MetaFlow/components/CompactPageSelect';
import type { CacBasis, LLCurrency, LLFilters, LeadLifetimePreset } from '@/types/lead-lifetime';
import type { LeadLifetimeFiltersState } from '../hooks/useLeadLifetimeFilters';
import { useMoney } from './currency';
import { fmtLL, objectiveLabel } from './format';

const PRESETS: Array<{ value: LeadLifetimePreset; label: string }> = [
    { value: 'today', label: 'Hoje' },
    { value: 'yesterday', label: 'Ontem' },
    { value: '7d', label: '7D' },
    { value: 'this_month', label: 'Mês atual' },
    { value: 'last_month', label: 'Mês passado' },
    { value: '60d', label: '60D' },
    { value: '90d', label: '90D' },
];

interface FiltersProps {
    state: LeadLifetimeFiltersState;
    projects: Array<{ id: number; name: string }> | undefined;
    isOwner: boolean;
    options: LLFilters | undefined;
    busy?: boolean;
}

const segBtn = (active: boolean) =>
    `rounded px-2 py-1 text-[11px] font-medium transition-colors ${
        active ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
    }`;

/** Objetivos de campanha que entram no custo. Nenhum marcado = todos. */
function ObjectiveSelect({ value, onChange, options }: { value: string[]; onChange: (v: string[]) => void; options: LLFilters['objectives'] }) {
    const money = useMoney();
    const [open, setOpen] = useState(false);
    const label = value.length === 0 ? 'Todos os objetivos' : value.map(objectiveLabel).join(', ');
    const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex h-8 w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 text-xs sm:w-56"
                title="Quais objetivos de campanha entram no gasto (e no CAC)"
            >
                <span className="truncate">{label}</span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute z-50 mt-1 w-72 rounded-md border border-border bg-popover p-1 shadow-lg">
                        <button type="button" onClick={() => onChange([])} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-accent">
                            <span className="w-4">{value.length === 0 && <Check className="h-3.5 w-3.5" />}</span>
                            Todos os objetivos
                        </button>
                        {options.map((o) => (
                            <button key={o.value} type="button" onClick={() => toggle(o.value)} className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-accent">
                                <span className="w-4">{value.includes(o.value) && <Check className="h-3.5 w-3.5" />}</span>
                                <span className="flex-1">{objectiveLabel(o.value)}</span>
                                <span className="tabular-nums text-muted-foreground">{money.fmt(o.spend)} · {fmtLL.num(o.conversations)} conv.</span>
                            </button>
                        ))}
                        {options.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">Sem gasto no período</p>}
                    </div>
                </>
            )}
        </div>
    );
}

/** Barra de filtros: projeto, página, período, objetivos e base do CAC. */
export function Filters({ state, projects, isOwner, options, busy }: FiltersProps) {
    const basisBtn = (b: CacBasis, label: string, hint: string) => (
        <button type="button" title={hint} onClick={() => state.setBasis(b)} className={segBtn(state.basis === b)}>
            {label}
        </button>
    );
    return (
        <div className="flex flex-col items-stretch gap-2 rounded-lg border border-border bg-card p-2 sm:flex-row sm:flex-wrap sm:items-center md:gap-3 md:p-3">
            <div className={`flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center md:gap-3 ${busy ? 'pointer-events-none opacity-50' : ''}`} aria-busy={busy}>
                <CompactProjectSelect value={state.projectId} onChange={state.setProjectId} projects={projects} isOwner={isOwner} />
                <CompactPageSelect value={state.pageId} onChange={state.setPageId} pages={options?.pages} />
                <ObjectiveSelect value={state.objectives} onChange={state.setObjectives} options={options?.objectives || []} />
                <div className="hidden h-6 w-px bg-border lg:block" />
                <div className="flex flex-wrap items-center gap-0.5 rounded-md border border-border p-0.5">
                    {PRESETS.map((p) => (
                        <button key={p.value} type="button" onClick={() => state.setPreset(p.value)} className={segBtn(state.preset === p.value)}>
                            {p.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                    {(['BRL', 'USD'] as LLCurrency[]).map((c) => (
                        <button
                            key={c}
                            type="button"
                            title={c === 'USD' ? 'Converte pela mesma cotação usada no cálculo' : 'Valores como o backend calculou'}
                            onClick={() => state.setCurrency(c)}
                            className={segBtn(state.currency === c)}
                        >
                            {c === 'BRL' ? 'R$' : 'US$'}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-0.5 rounded-md border border-border p-0.5">
                    <span className="px-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">CAC por</span>
                    {basisBtn('lead', 'Lead', 'Gasto ÷ leads que iniciaram conversa na messageria (mensagem, “Começar” ou comentário). Importação em massa fica de fora.')}
                    {basisBtn('conversation', 'Conversa Meta', 'Gasto ÷ conversas iniciadas atribuídas pela Meta aos anúncios (janela de 7 dias). Comparativo.')}
                </div>
            </div>
            {busy && (
                <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground sm:ml-auto">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    carregando — filtros travados
                </span>
            )}
        </div>
    );
}
